import { type ComponentType, useMemo } from 'react'

import { Checkbox } from '@/components/ui/checkbox.tsx'
import { STORY_TYPES } from '@/constants/STORY_TYPES.ts'
import { useStoryInteractionContext } from '@/contexts/StoryInteractionContext.tsx'
import { cn } from '@/lib/utils.ts'

type WithSelectableOptions<T> = {
  getId: (props: T) => string
  getGroupName: (props: T) => StoryGroup
  getStoryType: (props: T) => StoryType
}

export const withSelectable =
  <T extends object>(WrappedComponent: ComponentType<T>, options: WithSelectableOptions<T>) =>
  (props: T) => {
    const id = options.getId(props)
    const { selectedStoryIds, selectedGroupName, addSelectedStoryIds, removeSelectedStoryIds } =
      useStoryInteractionContext()
    const isSelected = useMemo(() => selectedStoryIds.has(id), [selectedStoryIds, id])
    const isShow = !selectedGroupName || selectedGroupName === options.getGroupName(props)

    return (
      <div>
        <WrappedComponent {...props} />
        {isShow && (
          <div className="m-2 absolute right-0 top-0 h-5 flex items-center">
            <Checkbox
              variant="custom"
              size="md"
              checked={isSelected}
              className={cn({
                'pointer-events-none invisible hidden': options.getStoryType(props) === STORY_TYPES.RELEASE,
              })}
              onClick={(e) => {
                e.stopPropagation()
                if (isSelected) {
                  removeSelectedStoryIds([id])
                } else {
                  addSelectedStoryIds([id])
                }
              }}
            />
          </div>
        )}
      </div>
    )
  }
